import { statementActivity, SummaryItem } from "./types";
import { PaginatedResponse } from "shared/types";
import { instance } from "api/apiBase";

export const getCSVLineItems = async (statementId: string, search: string) => {
  const allLineItems: statementActivity[] = [];
  let page = 1;
  let hasMore = true;

  const per_page = 100;

  while (hasMore) {
    const response: PaginatedResponse<statementActivity> = await instance
      .get(`/portal/v1/statements/${statementId}/activities`, {
        params: {
          per_page: per_page,
          page,
          search
        }
      })
      .then(({ data }) => data);

    allLineItems.push(...response.items);
    // @ts-expect-error -- hasMore is not defined in the response type
    hasMore = response.has_more || response.hasMore;
    page++;
  }

  return allLineItems.map((item: statementActivity) => ({
    ID: item.id,
    Item: item.description,
    Name: item.metadata.name,
    Email: item.metadata.email,
    Property: item.metadata.one_line_address,
    Unit: item.metadata.unit,
    Amount: item.amount.cents / 100
  }));
};

export const getFeeName = (name: FeeType): string => {
  switch (name) {
    case FeeType.CreditReport:
      return FeeNames.CreditReportFee;
    case FeeType.IdentityVerification:
      return FeeNames.IdentityVerificationFee;
    case FeeType.CreditCardProcessingFee:
      return FeeNames.CreditCardProcessingFee;
    case FeeType.ApplicationFee:
      return FeeNames.ApplicationFee;
    case FeeType.VerifiedIncomeVerification:
      return FeeNames.VerifiedIncomeVerificationFee;
    case FeeType.EvictionReport:
      return FeeNames.EvictionReportFee;
    case FeeType.CriminalReport:
      return FeeNames.CriminalReportFee;
    default:
      return name;
  }
};

export const normalizeData = (data: SummaryItem[]) => {
  let total = 0;

  const items = data.map((property) => {
    let propertyTotal = 0;
    const stats: Record<
      string,
      { name: string; feeInCents: number; count: number }
    > = {};

    property.items.forEach(({ items }) => {
      items?.forEach(({ name, fee: { cents } }) => {
        const normalizedName =
          name.includes("Boom screen application") ||
          name.includes("BoomScreen Application fee")
            ? "BoomScreen Application fee"
            : name.includes("BoomScreen credit card fee")
              ? "BoomScreen credit card fee"
              : name.split(" #")[0].trim();

        const uniqueKey = `${normalizedName}_${cents}`;

        if (!(uniqueKey in stats))
          stats[uniqueKey] = { name: normalizedName, feeInCents: 0, count: 0 };

        stats[uniqueKey].feeInCents += cents;
        stats[uniqueKey].count += 1;

        propertyTotal += cents;
      });
    });

    total += propertyTotal;

    return {
      ...property,
      stats,
      propertyTotal
    };
  });

  return {
    total,
    items
  };
};

// verfications that are free and should not be displayed in the billing
export const freeItems = [
  "submission_disclosure",
  "terms_and_conditions",
  "rental_application",
  "housing_history",
  "rental_verification",
  "income_verification"
];

export enum FeeType {
  CreditReport = "credit_report",
  CriminalReport = "criminal_report",
  EvictionReport = "eviction_report",
  HousingHistory = "housing_history",
  RentalVerification = "rental_verification",
  RentalApplication = "rental_application",
  IncomeVerification = "income_verification",
  TermsAndConditions = "terms_and_conditions",
  IdentityVerification = "identity_verification",
  SubmissionDisclosure = "submission_disclosure",
  CreditCardProcessingFee = "credit_card_processing_fee",
  ApplicationFee = "application_fee",
  PartnersFee = "partners_fee",
  VerifiedIncomeVerification = "verified_income_verification"
}

// TODO: Rewrite to obj
export enum FeeNames {
  CreditReportFee = "Screening Credit Report and Scores",
  EvictionReportFee = "Screening Eviction Report",
  CriminalReportFee = "Screening Criminal Report",
  IdentityVerificationFee = "Screening Identity Verification",
  ApplicationFee = "Screening Application Fees",
  CreditCardProcessingFee = "Screening Transaction Fees",
  VerifiedIncomeVerificationFee = "Screening Verified Income Verification"
}
